// Multi-level assignment utilities
// Roles ordered from most senior to least senior
export const ROLE_ORDER = ['senior', 'middle', 'junior'];
export const ROLE_LABELS = { senior: 'Senior', middle: 'Middle', junior: 'Junior' };

/**
 * Normalize assignment data from a lead, handling both old (single assignedTo)
 * and new (assignedRoles) formats.
 *
 * @param {object} leadData - The lead data object (e.g. from Firestore `data` field)
 * @returns {{ assignedRoles: { senior, middle, junior }, assignedUserIds: string[], assignedTo: object|null }}
 */
export function normalizeAssignmentData(leadData) {
    if (!leadData) {
        return {
            assignedRoles: { senior: null, middle: null, junior: null },
            assignedUserIds: [],
            assignedTo: null
        };
    }

    // New format — assignedRoles exists
    if (leadData.assignedRoles) {
        return {
            assignedRoles: {
                senior: leadData.assignedRoles.senior || null,
                middle: leadData.assignedRoles.middle || null,
                junior: leadData.assignedRoles.junior || null
            },
            assignedUserIds: leadData.assignedUserIds || [],
            assignedTo: leadData.assignedTo || null
        };
    }

    // Legacy format — only assignedTo exists, treat as senior
    if (leadData.assignedTo?.userId) {
        return {
            assignedRoles: {
                senior: leadData.assignedTo,
                middle: null,
                junior: null
            },
            assignedUserIds: [leadData.assignedTo.userId],
            assignedTo: leadData.assignedTo
        };
    }

    // No assignment at all
    return {
        assignedRoles: { senior: null, middle: null, junior: null },
        assignedUserIds: [],
        assignedTo: null
    };
}

/**
 * Build all assignment-related fields for a Firebase write from a roles object.
 *
 * @param {{ senior, middle, junior }} roles - The assignedRoles object (each value is { userId, firstName, lastName } or null)
 * @returns {{ assignedRoles, assignedUserIds: string[], assignedTo: object|null }}
 */
export function buildAssignmentUpdate(roles) {
    const assignedRoles = {
        senior: roles.senior || null,
        middle: roles.middle || null,
        junior: roles.junior || null
    };

    // Deduplicated array of all assigned user IDs
    const assignedUserIds = [...new Set(
        ROLE_ORDER
            .map(role => assignedRoles[role]?.userId)
            .filter(Boolean)
    )];

    // Most senior assigned person (for backwards compat)
    const assignedTo = assignedRoles.senior || assignedRoles.middle || assignedRoles.junior || null;

    return { assignedRoles, assignedUserIds, assignedTo };
}

/**
 * Get role display labels, using company-specific overrides if available.
 * Falls back to default labels for any role not overridden.
 *
 * @param {object|null} companyData - The company document data (from CompanyContext)
 * @returns {{ senior: string, middle: string, junior: string }}
 */
export function getRoleLabels(companyData) {
    if (!companyData?.roleLabels) return ROLE_LABELS;
    return {
        senior: companyData.roleLabels.senior || ROLE_LABELS.senior,
        middle: companyData.roleLabels.middle || ROLE_LABELS.middle,
        junior: companyData.roleLabels.junior || ROLE_LABELS.junior,
    };
}
